import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkSession } from 'redux/slices/authSlice';
import { fetchPinnedDeals } from 'redux/slices/dealsSlice';
import { fetchUserDetails } from 'redux/slices/userDetailsSlice';
import { updateUiState } from 'redux/slices/uiSlice';
import { loadRouteChunkFor } from 'views/lazy';

/**
 * How long the boot loader may stay up before it releases regardless of what is
 * still in flight. A safety valve only: `onAuthStateChanged` and
 * `fetchUserDetails` both resolve in the normal case, but neither has a timeout
 * of its own, and a hung request must not leave the user staring at a spinner.
 */
const MAX_BOOT_MS = 10000;

/**
 * Application-wide initialization, and the single gate for the boot loader.
 *
 * It performs:
 * 1. Firebase session check on mount.
 * 2. Fetches user details and pinned deals upon successful login.
 * 3. Tracks whether the app is ready to be displayed.
 *
 * This is the ONLY place that decides when the app is bootable.
 * `AuthenticatedRoutes` used to run a second `checkSession()` and blank the
 * screen while it was in flight; it no longer does, because this hook has
 * already settled `isLoggedIn` before `Routes` renders.
 *
 * `isInitializing` is intentionally monotonic: once the app has booted it never
 * flips back to `true`, so a later refetch shows its own in-page loader rather
 * than tearing the whole page down.
 *
 * The gate also waits on the *initial route's chunk*, whose download is started
 * here on mount rather than by `<Suspense>` after `Routes` renders. Two
 * problems, one change: the chunk used to be requested only once auth and user
 * details had both resolved, so it was serialized behind them; and the boot
 * loader used to tear down before the view existed, leaving a blank frame that
 * `DeferredLoadingPage` could only hide, not remove. Holding the gate until the
 * chunk resolves hands the boot loader off directly to the view, and leaves
 * `DeferredLoadingPage` covering what it was designed for: in-app navigation to
 * a cold chunk.
 *
 * @returns {Object} { isInitializing }
 */
const useAppInit = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const fetching = useSelector((state) => state.userDetails.fetching);
  const [isInitializing, setIsInitializing] = useState(true);
  const bootedRef = useRef(false);

  const userId = useSelector((state) => state.userDetails.userDetails?.id);
  const selectedDealsMemberId = useSelector(
    (state) => state.uiStates.selectedDealsMemberId,
  );

  /**
   * Whether the deal-list owner has been seeded from user details yet.
   *
   * `selectedDealsMemberId` cannot answer this on its own: `null` is both its
   * un-seeded start *and* what the member picker stores for "All Deals". A
   * separate flag keeps "not decided yet" distinct from "deliberately the whole
   * team", so the seed runs exactly once and never undoes the user's choice.
   */
  const [dealsOwnerSeeded, setDealsOwnerSeeded] = useState(false);

  const [routeChunkReady, setRouteChunkReady] = useState(false);

  const finishBoot = () => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    setIsInitializing(false);
  };

  /**
   * Whether this boot's `checkSession()` has settled.
   *
   * `isLoggedIn === null` cannot answer this: `auth.isLoggedIn` is on the
   * redux-persist whitelist, so REHYDRATE replaces the slice's `null` with
   * whatever the last page wrote -- before `checkSession` has even started. A
   * stale `false` (a tab closed before the login persisted) released the gate
   * as "logged out", `AuthenticatedRoutes` bounced a signed-in user to the sign
   * in page, and that page then sent them to their default route instead of
   * the one they opened.
   */
  const [sessionChecked, setSessionChecked] = useState(false);

  // Initial session check on mount
  useEffect(() => {
    let cancelled = false;
    const settle = () => {
      if (!cancelled) setSessionChecked(true);
    };
    dispatch(checkSession()).then(settle, settle);
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  // Start the initial route's chunk download in parallel with the session
  // check, and report when it settles.
  //
  // `settle` rather than `resolve`: a chunk that fails to load must release the
  // gate, not pin the boot spinner behind it. `lazyWithRetry` owns the retry and
  // reload path from `<Suspense>`, and it can only do that once the view is
  // actually rendering. An unrecognised path has no chunk and resolves
  // immediately.
  useEffect(() => {
    let cancelled = false;
    const settle = () => {
      if (!cancelled) setRouteChunkReady(true);
    };
    const chunk = loadRouteChunkFor(window.location.pathname);
    if (chunk) {
      chunk.then(settle, settle);
    } else {
      settle();
    }
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch critical user data once logged in
  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchUserDetails());
    }
  }, [isLoggedIn, dispatch]);

  // Seed the deal-list owner and its sidebar label from user details, the moment
  // user details settle -- which the boot gate below also waits for, so this
  // lands before the first paint of the app.
  //
  // It used to be the Deals view that did this, on mount and keyed on
  // `allTeamMembers` as well as `userDetails`. That ran after the whole
  // dashboard had painted against `null`, which meant a second
  // `fetchPinnedDeals` here, a second `deals/list` in the view 3ms after the
  // first, and the sidebar's label visibly changing from "All Deals" to
  // "My Deals" under a table that had already rendered.
  //
  // A failed user-details fetch still marks the seed done: the owner stays
  // `null` (the whole team), which is what it would have been anyway, and
  // nothing downstream is left waiting on a value that is not coming.
  useEffect(() => {
    if (dealsOwnerSeeded) return;
    // `fetching` is null initially, true while in flight, false when settled.
    if (fetching !== false) return;
    if (userId) {
      dispatch(updateUiState({ key: 'selectedDealsMemberId', value: userId }));
      dispatch(updateUiState({ key: 'selectedDealsLabel', value: 'My Deals' }));
    }
    setDealsOwnerSeeded(true);
  }, [userId, fetching, dealsOwnerSeeded, dispatch]);

  // Held until the seed has landed, so this fires once against the real owner
  // rather than once against `null` and again against the user id. Later
  // changes from the member picker still refetch, which is the point of having
  // `selectedDealsMemberId` in the dependency list at all.
  useEffect(() => {
    if (!isLoggedIn) return;
    if (!dealsOwnerSeeded) return;
    dispatch(fetchPinnedDeals(selectedDealsMemberId));
  }, [isLoggedIn, dispatch, selectedDealsMemberId, dealsOwnerSeeded]);

  // Determine readiness:
  // - Until this boot's `checkSession` settles, `isLoggedIn` may be a value
  //   rehydrated from storage rather than a real answer: wait.
  // - `false` means checked and logged out: nothing else to load, we are ready.
  // - `true` means wait for the user-details fetch to settle (success or failure).
  //
  // Either way the initial route's chunk must also have settled, so that the
  // boot loader hands off to a view that can paint immediately.
  useEffect(() => {
    if (!routeChunkReady) {
      return;
    }

    if (!sessionChecked || isLoggedIn === null) {
      return;
    }

    if (isLoggedIn === false) {
      finishBoot();
      return;
    }

    // 'fetching' is null initially, true while fetching, and false when done.
    if (fetching === false) {
      finishBoot();
    }
  }, [isLoggedIn, fetching, routeChunkReady, sessionChecked]);

  useEffect(() => {
    const timer = setTimeout(finishBoot, MAX_BOOT_MS);
    return () => clearTimeout(timer);
  }, []);

  return { isInitializing };
};

export default useAppInit;
